import { invariant } from '@seen/core/invariant'
import { OrderCategory } from '@seen/core/models/orders/constants'
import type { Order } from '@seen/core/models/orders/Order'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { OrderSignatureTask } from '@seen/core/models/tasks/standard/orderSignatureTask'
import { Role } from '@seen/core/roles'
import { Badge, Button } from '@seen/ui'
import { IconCheck, IconEdit, IconWriting } from '@tabler/icons-react'
import type { JSX } from 'react'
import {
  createTaskFilter,
  useMarkRelatedInboxItemsAsDone,
} from '#src/hooks/inbox/index.ts'
import { useCreateIfNoneExistsMutation } from '#src/hooks/useCreateIfNoneExistsMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import { toast } from '#src/utils/toast.ts'

interface OrderSignatureProps {
  order: Order
  onEditOrder?: () => void
  isTwoColumn?: boolean
}

export function OrderSignature({
  order,
  onEditOrder,
  isTwoColumn = false,
}: OrderSignatureProps): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const practitioner = useCurrentPractitioner()
  const showAdminActions = usePortalStore((state) => state.showAdminActions)
  const {
    data: signatureTask,
    isPending,
    refetch,
  } = useSearchOne(OrderSignatureTask, OrderSignatureTask.searchQuery(order))
  const transactionMutation = useTransactionMutation()
  const patchOrder = usePatchMutation<Order>()

  const activePCPQuery = useSearch(
    ExtendedPractitioner,
    ExtendedPractitioner.queryByRoles(Role.PCP)
  )

  const createOrderSignatureTaskMutation =
    useCreateIfNoneExistsMutation<OrderSignatureTask>({
      mutation: {
        onSuccess: () => {
          void refetch()
        },
      },
    })

  const markRelatedInboxItemsAsDone = useMarkRelatedInboxItemsAsDone({
    silent: true,
  })

  if (isPending) {
    return null
  }

  if (isReadOnly) {
    return null
  }

  const isPCP = practitioner.isRole(Role.PCP)
  const isEscort = order.category === OrderCategory.EscortServices

  if (order.status === 'draft') {
    if (!onEditOrder) {
      return null
    }

    const willSign = isPCP && !isEscort
    const nextStatus: 'active' | 'on-hold' =
      isPCP || isEscort ? 'active' : 'on-hold'

    const handleSubmit = (): void => {
      patchOrder.mutate(
        {
          instance: order,
          mutateFn: (resource) => {
            resource.status = nextStatus
            if (willSign) {
              resource.sign(practitioner.reference)
            }
          },
        },
        {
          onSuccess: () => {
            toast.success(
              willSign
                ? 'Order successfully signed.'
                : 'Order successfully submitted.'
            )
          },
        }
      )
    }

    return (
      <div
        className={
          isTwoColumn
            ? 'flex flex-col items-start gap-2'
            : 'flex flex-row items-center gap-2'
        }
      >
        <Button
          variant="outline"
          size="sm"
          disabled={!order.isEditable}
          onClick={onEditOrder}
        >
          <IconEdit className="mr-2 h-4 w-4" />
          Edit Order
        </Button>
        <Button
          variant="outline"
          color="green"
          size="sm"
          loading={patchOrder.isPending}
          disabled={!order.isEditable || !order.participant}
          onClick={handleSubmit}
        >
          {willSign ? (
            <IconWriting className="mr-2 h-4 w-4" />
          ) : (
            <IconCheck className="mr-2 h-4 w-4" />
          )}
          {willSign ? 'Submit Draft & Sign' : 'Submit Draft'}
        </Button>
      </div>
    )
  }

  if (isEscort) {
    return null
  }

  const handleCreate = (): void => {
    const pcp = activePCPQuery.data?.find((p) => p.isInternal)
    invariant(pcp, 'PCP not found')

    createOrderSignatureTaskMutation.mutate({
      instance: new OrderSignatureTask({
        order,
        requester: practitioner.reference,
        owner: pcp.reference,
      }),
      query: OrderSignatureTask.searchQuery(order),
    })
  }

  const handleSign = (): void => {
    if (!signatureTask) {
      return
    }

    transactionMutation.mutate(
      (transaction) => {
        transaction.patch(signatureTask, (task) => {
          task.state = 'done'
        })

        transaction.patch(order, (order) => {
          order.sign(practitioner.reference)
        })
      },
      {
        onSuccess: () => {
          markRelatedInboxItemsAsDone(
            createTaskFilter([
              {
                taskReferenceString: signatureTask.reference.reference,
                state: 'done',
              },
            ])
          )
          toast.success('Order signed.')
        },
      }
    )
  }

  if (!signatureTask) {
    return (
      <div className="flex items-center gap-2">
        {showAdminActions && (
          <Button
            onClick={handleCreate}
            variant="outline"
            loading={createOrderSignatureTaskMutation.isPending || isPending}
          >
            Request Signatures
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-row items-center">
      {(() => {
        if (!order.signedDate) {
          if (isPCP || showAdminActions) {
            return (
              <Button
                variant="outline"
                color="green"
                size="sm"
                loading={transactionMutation.isPending}
                onClick={handleSign}
              >
                <IconWriting className="mr-2 h-4 w-4" />
                {isPCP ? 'Sign' : 'Sign on Behalf'}
              </Button>
            )
          }
          return null
        } else {
          return (
            <Badge variant="outline">
              <IconWriting className="h-4 w-4" />
              Signed
            </Badge>
          )
        }
      })()}
    </div>
  )
}
